import React, {FC, memo} from 'react';
import { FormattedMessage } from 'react-intl';
import { generatePath, Link } from 'react-router-dom';

// Import Styles
import styled from 'styled-components';
import colors from '../../../../config/colors';
import { STATUSES } from '../../../../config/config';
import routes from '../../../../config/routes';
import generalMessages from '../../../../containers/generalMessages';
import { Job } from '../../../../models/jobs';
import { getCountryByCode } from '../../../../utils/country';
import { formatDatefromString } from '../../../../utils/date';
import { getPublicUrl } from '../../../../utils/ImageCacheService';
import H3 from '../../../Headings/H3';
import { AddOptions } from '../../../Panel/Ads';
import P from '../../../Paragraph';
import {TagSmRound} from '../../../Tag';


interface IActiveJob {
  item: Job;
  status?: boolean;
  unSave?: Function;
}

const ActiveJob:FC<IActiveJob> = ({item, status, unSave}) => {

    return (
        <ActiveJobWrapper className={item.package_type === 'featured' ? 'featured' : item.package_type === 'hot' ? 'hot' : ''}>
            <ActiveJobLink to={generatePath(routes.JobsDetailByUrlSlug, {'industry': item.field_of_work, 'slug' : item.slug})}>
                <MainInfo>
                    <Logo>
                        {item.publisher === 'partner' ?
                            item.partner_logo ?
                                <span>
                                    {item.partner_file_name ? <img src={getPublicUrl(item.partner_file_name)}  alt={item.company_name} /> : item.company_name}
                                </span>
                            : <span><svg><use xlinkHref={`#company-logo-placeholder`} /></svg></span>
                            :
                            item.company_logo ?
                                <span>
                                    {item.company_file_name ? <img src={getPublicUrl(item.company_file_name)}  alt={item.company_name} /> : item.company_name}
                                </span>
                        : <span><svg><use xlinkHref={`#company-logo-placeholder`} /></svg></span>}
                    </Logo>
                    <Description>
                        <H3>{item.title}</H3>
                        <P>{item.company_name}</P>
                        <P><FormattedMessage {...generalMessages.JobPublished} /> {formatDatefromString(item.published_at)}</P>
                    </Description>
                </MainInfo>
                <AdditionalInfo>
                    <Location>
                    {item.locations && item.remote === 0 ?
                        <span className="flex">
                            <svg><use xlinkHref={`#location`} /></svg>
                            {item.locations && item.locations.length > 0 && item.locations[0].country ? getCountryByCode(item.locations[0].country) : null}
                            {item.locations && item.locations.length > 0 && item.locations[0].city ? ', ' : null}
                            <div className={`cities ${item.locations.length > 2 ? 'textOverflow' : ''}`}>
                              <div className="citiesInner">
                                {item.locations && item.locations.length > 0 ?
                                    item.locations.map((location: any, i: number) => {
                                        return (
                                            <span key={i}>
                                                {location.city}
                                            </span>
                                        )
                                    })
                                : null}
                                </div>
                            </div>
                        </span>
                    : item.remote === 1 ?
                        <span className="flex">
                            <svg><use xlinkHref={`#location`} /></svg>
                            <FormattedMessage {...generalMessages.WorkFromHome} />
                        </span>
                    : null}
                    </Location>
                    <JobTags>
                        <P><FormattedMessage {...generalMessages.Expires} />
                        {formatDatefromString(item.expires_at)}</P>
                        {item.employment_contract ?
                            <TagSmRound theme="orange">{item.employment_contract}</TagSmRound>
                        : null}
                        {status ?
                            item.status === STATUSES.process || item.status === STATUSES.expired || item.status === STATUSES.preselection || item.status === STATUSES.selection || item.status === STATUSES.service_requested_preselection || item.status === STATUSES.service_requested_selection ?
                                    <TagSmRound theme='blue'><FormattedMessage {...generalMessages.Expired} /></TagSmRound>
                                : item.status === STATUSES.inactive ?
                                    <TagSmRound theme='gray'><FormattedMessage {...generalMessages.ApplyingClosed} /></TagSmRound>
                                :
                                    <TagSmRound theme='green'><FormattedMessage {...generalMessages.Active} /></TagSmRound>
                        : null}
                    </JobTags>
                </AdditionalInfo>
            </ActiveJobLink>
            {unSave ?
                <AddOptions onClick={() => unSave(item.unique_id)}>
                    <svg><use xlinkHref={`#delete`} /></svg>
                    <FormattedMessage {...generalMessages.Remove} />
                </AddOptions>
            : null}
        </ActiveJobWrapper>
    )
}

const ActiveJobWrapper = styled.div`
    position: relative;
    margin-bottom: 20px;
    padding: 15px 20px;
    background: ${colors.WHITE};
    transition: all 200ms ease-in-out;

    @media (min-width: 992px) {

        @supports not (-webkit-overflow-scrolling: touch) {

        &:hover {
            ${AddOptions} {
            opacity: 1;
            pointer-events: auto;
            display: flex;
            }
        }
    }
}

@supports (-webkit-overflow-scrolling: touch) {
&:active {
  background: ${colors.BACKGROUND_GRAY};

  ${AddOptions} {
    opacity: 1;
    pointer-events: auto;
    display: flex;
  }
}
}
`;

const ActiveJobLink = styled(Link)`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    @media (max-width: 680px) {
       flex-direction: column;
    }
`;

const MainInfo = styled.div`
    display: flex;
    @media (max-width: 580px) {
       flex-direction: column;
    }
`;

const Logo = styled.div`
    span {
        width: 100px;
        height: 100px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        margin-right: 30px;
    }
    img, svg {
        width: 100px;
        height: 100px;
        object-fit: contain;
        padding: 3px;
    }
`;

const Description = styled.div`
    h3 {
        font-size: 20px;
        line-height: 30px;
    }
    p {
        font-family: "Gilroy-Medium";
        color: ${colors.GRAY_MEDIUM};
    }
    @media (max-width: 580px) {
       margin-top: 10px;
    }
`;

const AdditionalInfo = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    min-height: 100px;
    @media (max-width: 680px) {
       min-height: auto;
       margin-top: 10px;
       width: 100%;
    }
`;

const Location = styled.div`
    font-family: "Gilroy-Medium";
    color: ${colors.GRAY_MEDIUM};

    svg {
        margin-right: 5px;
        align-self: baseline;
        stroke: ${colors.PRIMARY_PURPLE};
    }

    .flex {
        display: flex;
        align-items: center;
    }
    .cities {
        margin-left: 5px;
        position: relative;

        &.textOverflow {
          padding-right: 15px;

          &:after {
            content: "...";
            position: absolute;
            top: 0;
            right: 0;
          }

          .citiesInner {
            max-width: 180px;
            overflow: hidden;
            white-space: nowrap;
          }
        }

        span {
            display: inline-block;
            &:before {
                content: "";
                width: 1px;
                height: 12px;
                display: inline-block;
                vertical-align: middle;
                background: ${colors.GRAY_MEDIUM};
                margin: 0 5px;
            }

            &:first-of-type {
                &:before {
                    display: none;
                }
            }
        }
    }
`;

const JobTags = styled.div`
    margin-top: auto;
    text-align: right;
    @media (max-width: 680px) {
       position: absolute;
       top: 14px;
    }
    p {
        font-family: "Gilroy-Medium";
    }
    span {
        margin-right: 0;
    }
`;

export default memo(ActiveJob);
